import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Cookie, Shield, Settings2, X } from "lucide-react";

const KEY = "vdh_cookie_consent_v1";

function readConsent() {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "null");
  } catch {
    return null;
  }
}

export function saveConsent(c) {
  localStorage.setItem(KEY, JSON.stringify({ ...c, savedAt: new Date().toISOString() }));
  window.dispatchEvent(new CustomEvent("vdh-consent-changed", { detail: c }));
}

export function getConsent() {
  return readConsent();
}

export default function CookieBanner() {
  const [consent, setConsent] = useState(null);
  const [customizing, setCustomizing] = useState(false);
  const [prefs, setPrefs] = useState({ necessary: true, analytics: false, marketing: false });

  useEffect(() => {
    setConsent(readConsent());
  }, []);

  if (consent !== null) return null;

  const acceptAll = () => {
    saveConsent({ necessary: true, analytics: true, marketing: true });
    setConsent(true);
  };
  const rejectAll = () => {
    saveConsent({ necessary: true, analytics: false, marketing: false });
    setConsent(true);
  };
  const saveCustom = () => {
    saveConsent(prefs);
    setConsent(true);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 120, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 120, opacity: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="fixed bottom-3 inset-x-3 md:bottom-5 md:right-5 md:left-auto md:max-w-md z-[95] vdh-safe-bottom"
        data-testid="cookie-banner"
        role="dialog"
        aria-label="Preferenze cookie"
      >
        <div className="rounded-2xl p-5 border border-white/15 shadow-2xl bg-[#0a0a0c] ring-1 ring-white/5">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[var(--vdh-pink)]/20 border border-[var(--vdh-pink)]/40 text-[var(--vdh-pink)] flex-none">
              <Cookie size={18} />
            </div>
            <div className="flex-1">
              <div className="font-display text-lg tracking-tight leading-tight">
                Cookie & Privacy
              </div>
              <p className="text-xs text-white/60 mt-1 leading-snug">
                Usiamo cookie per far funzionare il sito, misurare le performance e migliorare la tua esperienza. Puoi scegliere.{" "}
                <Link to="/cookie-policy" className="text-[var(--vdh-pink)] underline">
                  Cookie policy
                </Link>
                {" · "}
                <Link to="/privacy" className="text-[var(--vdh-pink)] underline">
                  Privacy
                </Link>
              </p>
            </div>
          </div>

          {customizing && (
            <div className="my-3 space-y-2">
              {[
                { key: "necessary", label: "Necessari", desc: "Richiesti per il funzionamento", locked: true },
                { key: "analytics", label: "Analitici", desc: "Per capire come usi il sito" },
                { key: "marketing", label: "Marketing / Notifiche", desc: "Push notification e remarketing" },
              ].map((row) => (
                <label
                  key={row.key}
                  className="flex items-start gap-3 p-2.5 rounded-xl bg-white/[0.03] border border-white/5 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    disabled={row.locked}
                    checked={row.locked ? true : prefs[row.key]}
                    onChange={(e) => setPrefs({ ...prefs, [row.key]: e.target.checked })}
                    className="mt-1 accent-[var(--vdh-pink)]"
                    data-testid={`cookie-pref-${row.key}`}
                  />
                  <div className="flex-1">
                    <div className="text-sm text-white/90 font-medium">{row.label}</div>
                    <div className="text-[11px] text-white/50">{row.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            {!customizing ? (
              <>
                <button
                  onClick={acceptAll}
                  data-testid="cookie-accept-all"
                  className="flex-1 min-w-[140px] inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-semibold text-black bg-gradient-to-b from-orange-400 to-orange-600 ring-1 ring-orange-300/60 shadow-[0_0_14px_rgba(250,204,21,0.4)]"
                >
                  <Shield size={14} /> Accetta tutto
                </button>
                <button
                  onClick={rejectAll}
                  data-testid="cookie-reject-all"
                  className="inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2.5 text-sm border border-white/15 text-white/80 hover:bg-white/5"
                >
                  <X size={14} /> Rifiuta
                </button>
                <button
                  onClick={() => setCustomizing(true)}
                  data-testid="cookie-customize"
                  className="inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2.5 text-sm border border-white/10 text-white/60 hover:bg-white/5"
                >
                  <Settings2 size={14} /> Personalizza
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={saveCustom}
                  data-testid="cookie-save"
                  className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-semibold text-black bg-gradient-to-b from-orange-400 to-orange-600"
                >
                  Salva preferenze
                </button>
                <button
                  onClick={() => setCustomizing(false)}
                  className="inline-flex items-center justify-center rounded-full px-4 py-2.5 text-sm border border-white/15 text-white/70"
                >
                  Indietro
                </button>
              </>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
